import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, Undo2, FileSpreadsheet, CheckCircle2, XCircle } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ImportHistory({ refreshKey = 0, onChanged }) {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/leads/import-batches");
      setItems(data || []);
    } catch (e) {
      // Silent — panel just stays empty on error.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const undo = async (b) => {
    if (!window.confirm(`Undo import "${b.filename}"? This will delete ${b.remaining} lead${b.remaining !== 1 ? "s" : ""} from this batch. This cannot be reversed.`)) return;
    setBusyId(b.id);
    try {
      const { data } = await api.post(`/leads/import-batches/${b.id}/undo`);
      toast.success(`Reverted — ${data.deleted} lead${data.deleted !== 1 ? "s" : ""} removed`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (!items.length) return null;

  return (
    <div className="solid-panel p-5" data-testid="import-history">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-[#1D63ED]" />
        <p className="text-sm font-semibold text-slate-800">Import history</p>
        <span className="text-xs text-slate-500">last {items.length}</span>
      </div>
      <ul className="divide-y divide-slate-200/70">
        {items.map((b) => {
          const undone = b.status === "undone";
          return (
            <li key={b.id} className="py-3 flex items-center gap-3" data-testid={`import-batch-${b.id}`}>
              <div className="w-8 h-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                <FileSpreadsheet className="w-4 h-4 text-[#1D63ED]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-slate-800 truncate">{b.filename || "upload.csv"}</p>
                  {undone ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200 uppercase tracking-wider font-medium flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Undone
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {b.count} imported
                  {b.remaining !== b.count && !undone && <span className="text-slate-400"> · {b.remaining} still present</span>}
                  {b.duplicates_count > 0 && <span className="text-slate-400"> · {b.duplicates_count} duplicates skipped</span>}
                  <span className="text-slate-400"> · {b.uploaded_by_name || "member"} · {timeAgo(b.uploaded_at)}</span>
                </p>
              </div>
              {!undone && b.remaining > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => undo(b)}
                  disabled={busyId === b.id}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 flex-shrink-0"
                  data-testid={`undo-batch-${b.id}`}
                >
                  <Undo2 className="w-4 h-4 mr-1.5" />
                  {busyId === b.id ? "Undoing…" : "Undo import"}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
